/**
 * External-projects API client (Phase 23 NAV-02 / NAV-03).
 *
 * IMPORTANT: this module DOES NOT route through services/api/client.ts. Doing so
 * would clear localStorage.monksheets_auth_token on any 401 (see client.ts:28),
 * which would log out the internal Google-SSO user whenever an external visitor
 * hits /api/external/me/projects without a session. We use fetch() directly with
 * credentials:'include' so the HttpOnly session cookie is sent, but no internal
 * token state is touched. This mirrors services/api/externalAuth.ts (Phase 14
 * Landmine #8 carry-over).
 */
import { ExternalApiError } from './externalAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export interface ExternalProjectNode {
  id: number;
  slug: string; // Phase 27 D-15.4: slug for kanban header chip
  name: string;
  parentId: number | null;
  state: string;
  isMember: boolean;
}

async function externalFetch<T>(method: string, path: string): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    type ErrorPayload = { error?: string; message?: string };
    const payload = (await res.json().catch(() => ({}))) as ErrorPayload;
    throw new ExternalApiError({
      statusCode: res.status,
      errorCode: typeof payload.error === 'string' ? payload.error : undefined,
      message:
        typeof payload.message === 'string'
          ? payload.message
          : (payload.error ?? 'Unknown error'),
    });
  }
  return (await res.json()) as T;
}

export async function getMyExternalProjects(): Promise<ExternalProjectNode[]> {
  const res = await externalFetch<{ data: ExternalProjectNode[] }>('GET', '/external/me/projects');
  return res.data;
}
